import { useEffect, useMemo, useRef, type CSSProperties } from "react";
import type { AppEntry } from "../registry/apps";
import { isPlaceholder, QUEST_GOAL, type Progress } from "../lib/progress";
import { appHref, formatInt, hostOf } from "../lib/format";

type MosaicProps = {
  apps: readonly AppEntry[];
  progress: Progress;
  reducedMotion: boolean;
};

type Span = { col: number; row: number };

type Tile =
  | { kind: "score"; key: string; span: Span; tilt: number }
  | { kind: "app"; key: string; span: Span; tilt: number; app: AppEntry }
  | { kind: "ghost"; key: string; span: Span; tilt: number; slot: number; variant: number };

const SPANS: Span[] = [
  { col: 3, row: 3 },
  { col: 2, row: 2 },
  { col: 4, row: 2 },
  { col: 2, row: 3 },
  { col: 3, row: 2 },
  { col: 2, row: 2 },
  { col: 5, row: 3 },
  { col: 3, row: 3 },
  { col: 2, row: 2 },
  { col: 4, row: 3 },
  { col: 3, row: 2 },
  { col: 2, row: 4 },
];

const APP_SLOTS = [2, 7, 11, 16, 22, 28, 33, 38];
const GHOST_COUNT = 40;

function hashTilt(n: number): number {
  const x = Math.sin(n * 12.9898) * 43758.5453;
  return (x - Math.floor(x)) * 3.2 - 1.6;
}

function buildTiles(apps: readonly AppEntry[]): Tile[] {
  const tiles: Tile[] = [];
  tiles.push({
    kind: "score",
    key: "score",
    span: { col: 4, row: 3 },
    tilt: -0.8,
  });

  apps.forEach((app, i) => {
    tiles.push({
      kind: "app",
      key: `app-${app.slug}`,
      span: SPANS[(i + 3) % SPANS.length] ?? { col: 3, row: 2 },
      tilt: hashTilt(i + 9),
      app,
    });
  });

  let ghostN = 1;
  for (let i = 0; i < GHOST_COUNT; i += 1) {
    tiles.push({
      kind: "ghost",
      key: `ghost-${i}`,
      span: SPANS[i % SPANS.length] ?? { col: 2, row: 2 },
      tilt: hashTilt(i + 21),
      slot: ghostN,
      variant: i % 8,
    });
    ghostN += 1;
  }

  // Score first, then interleave apps into the ghost wall at reserved indices.
  const ghosts = tiles.filter((t) => t.kind === "ghost");
  const appTiles = tiles.filter((t) => t.kind === "app");
  const score = tiles.find((t) => t.kind === "score");
  const wall: Tile[] = score ? [score] : [];

  let appCursor = 0;
  ghosts.forEach((g, i) => {
    const insertAt = APP_SLOTS.includes(i);
    if (insertAt) {
      const app = appTiles[appCursor];
      if (app) {
        wall.push(app);
        appCursor += 1;
      }
    }
    wall.push(g);
  });
  while (appCursor < appTiles.length) {
    const leftover = appTiles[appCursor];
    if (leftover) wall.push(leftover);
    appCursor += 1;
  }

  return wall;
}

export function Mosaic({ apps, progress, reducedMotion }: MosaicProps) {
  const wallRef = useRef<HTMLDivElement>(null);
  const tiles = useMemo(() => buildTiles(apps), [apps]);

  useEffect(() => {
    if (reducedMotion) return;
    const wall = wallRef.current;
    if (!wall) return;

    const onMove = (event: MouseEvent) => {
      const x = (event.clientX / window.innerWidth - 0.5) * 18;
      const y = (event.clientY / window.innerHeight - 0.5) * 14;
      wall.style.setProperty("--px", `${x}px`);
      wall.style.setProperty("--py", `${y}px`);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [reducedMotion]);

  return (
    <div className="mosaic-frame" aria-label="Mosaic of app screenshots">
      <div
        ref={wallRef}
        className={`mosaic ${reducedMotion ? "is-still" : ""}`}
      >
        {tiles.map((tile) => {
          const style = {
            "--cs": tile.span.col,
            "--rs": tile.span.row,
            "--tilt": `${tile.tilt}deg`,
          } as CSSProperties;

          if (tile.kind === "score") {
            return (
              <article key={tile.key} className="tile score" style={style}>
                <p className="score-kicker">
                  {progress.count === 0 ? "DAY ZERO" : "LIVE COUNT"}
                </p>
                <p className="score-num">
                  <span>{formatInt(progress.count)}</span>
                    <span className="score-of">/{formatInt(QUEST_GOAL)}</span>
                </p>
                <p className="score-foot">
                  {progress.count === 0
                    ? "The wall is empty. Start shipping."
                    : `${formatInt(progress.remaining)} slots still blank.`}
                </p>
              </article>
            );
          }

          if (tile.kind === "app") {
            return <AppTile key={tile.key} app={tile.app} style={style} />;
          }

          return (
            <div
              key={tile.key}
              className={`tile ghost v-${tile.variant}`}
              style={style}
              aria-hidden="true"
            >
              <span className="ghost-slot">SLOT {String(tile.slot).padStart(4, "0")}</span>
              <GhostMark variant={tile.variant} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AppTile({
  app,
  style,
}: {
  app: AppEntry;
  style: CSSProperties;
}) {
  const staged = isPlaceholder(app);
  const href = appHref(app.slug, app.url);
  const host = hostOf(href);
  const alt = staged
    ? `Placeholder preview for ${app.title} — example, not a live app`
    : `${app.title} — live app screenshot, ${host}`;

  const inner = (
    <>
      <img src={app.screenshot} alt={alt} width={1440} height={900} />
      <div className="tile-meta">
        <span className="tile-title">{app.title}</span>
        <span className="tile-host">{host}</span>
      </div>
      {staged ? (
        <span className="stamp">{app.example ? "EXAMPLE · NOT LIVE" : "COMING SOON"}</span>
      ) : (
        <span className="stamp live">OPEN ↗</span>
      )}
    </>
  );

  if (staged) {
    return (
      <div
        className="tile app is-staged"
        style={style}
        tabIndex={0}
        role="img"
        aria-label={`${app.title}, staged example, not live. Intended home ${host}.`}
      >
        {inner}
      </div>
    );
  }

  return (
    <a className="tile app" style={style} href={href}>
      {inner}
    </a>
  );
}

function GhostMark({ variant }: { variant: number }) {
  if (variant === 0) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        <rect x="8" y="8" width="64" height="64" fill="none" stroke="currentColor" strokeWidth="2" />
        <rect x="20" y="20" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <rect x="32" y="32" width="16" height="16" fill="currentColor" />
      </svg>
    );
  }
  if (variant === 1) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <circle key={i} cx={16 + (i % 3) * 24} cy={16 + Math.floor(i / 3) * 36 + 8} r="6" fill="currentColor" />
        ))}
      </svg>
    );
  }
  if (variant === 2) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        <path d="M10 40 H70 M40 10 V70" stroke="currentColor" strokeWidth="3" />
        <circle cx="40" cy="40" r="18" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }
  if (variant === 3) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        <path d="M8 64 L40 12 L72 64 Z" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M24 64 L40 36 L56 64" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    );
  }
  if (variant === 4) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        {Array.from({ length: 5 }, (_, i) => (
          <rect
            key={i}
            x={10 + i * 4}
            y={10 + i * 4}
            width={60 - i * 8}
            height={60 - i * 8}
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
          />
        ))}
      </svg>
    );
  }
  if (variant === 5) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        <path
          d="M8 52 Q24 8 40 40 Q56 72 72 28"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
        <circle cx="72" cy="28" r="4" fill="currentColor" />
      </svg>
    );
  }
  if (variant === 6) {
    return (
      <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
        <circle cx="40" cy="40" r="28" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="40" cy="40" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="40" cy="40" r="4" fill="currentColor" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 80 80" className="ghost-mark" aria-hidden="true">
      {Array.from({ length: 16 }, (_, i) => (
        <rect
          key={i}
          x={8 + (i % 4) * 16}
          y={8 + Math.floor(i / 4) * 16}
          width="12"
          height="12"
          fill={i % 3 === 0 ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1"
        />
      ))}
    </svg>
  );
}
